import {RemoveCircleIcon} from '@sanity/icons'
import {
  Box,
  Button,
  Card,
  Flex,
  Label,
  Menu,
  MenuButton,
  MenuItem,
  Spinner,
  Text,
  Tooltip,
} from '@sanity/ui'
import type React from 'react'
import {type ReactNode, useCallback, useMemo} from 'react'
import {type FormPatch, type ObjectItemProps, PatchEvent, type Path, useFormValue} from 'sanity'
import {set, unset} from 'sanity'

import {LANGUAGE_FIELD_NAME} from '../constants'
import type {InternationalizedArrayItem} from '../types'
import {getLanguageDisplay} from '../utils/getLanguageDisplay'
import {getToneFromValidation} from './getToneFromValidation'
import {useInternationalizedArrayContext} from './InternationalizedArrayContext'

function ChangeLanguageButton(props: {
  value: InternationalizedArrayItem
  path: Path
  onChange: (patch: FormPatch[] | PatchEvent | FormPatch) => void
}): ReactNode {
  const {value, onChange, path} = props
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const parentValue = useFormValue(path.slice(0, -1)) as InternationalizedArrayItem[]
  const {languages} = useInternationalizedArrayContext()

  const languageKeysInUse = useMemo(
    () => parentValue?.map((v) => v[LANGUAGE_FIELD_NAME]) ?? [],
    [parentValue],
  )

  // Changes the key of this item, ideally to a valid language
  const handleKeyChange = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const languageId = event?.currentTarget?.value

    if (!value || !languages?.length || !languages.find((l) => l.id === languageId)) {
      return
    }

    onChange([set(languageId, [LANGUAGE_FIELD_NAME])])
  }

  return (
    <MenuButton
      button={<Button fontSize={1} text={`Change "${value[LANGUAGE_FIELD_NAME]}"`} />}
      id={`${value[LANGUAGE_FIELD_NAME]}-change-key`}
      menu={
        <Menu>
          {languages.map((lang) => (
            <MenuItem
              disabled={languageKeysInUse.includes(lang.id)}
              fontSize={1}
              key={lang.id}
              text={lang.id.toLocaleUpperCase()}
              value={lang.id}
              // @ts-expect-error - fix typings
              onClick={handleKeyChange}
            />
          ))}
        </Menu>
      }
      popover={{portal: true}}
    />
  )
}

function RemoveButton({
  isDefault,
  readOnly,
  onChange,
}: {
  isDefault: boolean
  readOnly: boolean
  onChange: (patch: FormPatch[] | PatchEvent | FormPatch) => void
}): ReactNode {
  // Removes this item from the array
  const handleUnset = (): void => {
    onChange(unset())
  }

  return (
    <Tooltip
      animate
      disabled={!isDefault}
      content={
        <Text muted size={1}>
          Can&apos;t remove default language
        </Text>
      }
      fallbackPlacements={['right', 'left']}
      placement="top"
      portal
    >
      <span style={{paddingBottom: '2px'}}>
        <Button
          mode="bleed"
          icon={RemoveCircleIcon}
          tone="critical"
          disabled={readOnly || isDefault}
          onClick={handleUnset}
        />
      </span>
    </Tooltip>
  )
}

/**
 * Renders a single row of an internationalized array: a language label (or
 * "Change" menu for invalid keys), the value input, and a remove button.
 *
 * Key behaviours:
 * - Wraps `onChange` to intercept paste operations into empty Portable Text
 *   fields, ensuring `setIfMissing` and correct path prefixing.
 * - Detects items needing migration (missing `LANGUAGE_FIELD_NAME`) and hides
 *   the language label in that state.
 * - Shows a `MenuButton` with available languages when the current key is
 *   not a valid language.
 * - Disables the remove button for languages listed in `defaultLanguages`.
 * - Shows a `Spinner` when languages have not been loaded yet.
 * - Applies a `CardTone` based on validation state via `getToneFromValidation`.
 */
export default function InternationalizedInput(
  props: ObjectItemProps<InternationalizedArrayItem>,
): ReactNode {
  // Extract the original onChange to avoid dependency issues
  const originalOnChange = props.inputProps.onChange

  // Create a wrapped onChange handler to intercept patches for paste operations
  const wrappedOnChange = useCallback(
    (patches: unknown) => {
      // Ensure patches is an array before proceeding with paste logic
      // For single patch operations (like unset), pass through directly
      if (!Array.isArray(patches)) {
        // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
        return originalOnChange(patches as Parameters<typeof originalOnChange>[0])
      }

      // Check if this is a paste operation into an empty or uninitialized Portable Text field
      const valueField = props.value?.value
      const isEmptyOrUndefined =
        valueField === undefined ||
        valueField === null ||
        (Array.isArray(valueField) && valueField.length === 0)

      if (isEmptyOrUndefined) {
        // Check for insert patches that are trying to operate on a non-existent structure
        const hasProblematicInsert = patches.some((patch: {type?: string; path?: unknown[]}) => {
          // Ensure patch exists and has required properties
          if (!patch || typeof patch !== 'object') {
            return false
          }

          // Look for insert patches targeting the value field or direct array index
          if (
            patch.type === 'insert' &&
            patch.path &&
            Array.isArray(patch.path) &&
            patch.path.length > 0
          ) {
            // The path might be ['value', index] or just [index] depending on context
            const isTargetingValue = patch.path[0] === 'value' || typeof patch.path[0] === 'number'
            return isTargetingValue
          }
          return false
        })

        if (hasProblematicInsert) {
          // First, ensure the value field exists as an empty array if it doesn't
          const initPatch =
            valueField === undefined ? {type: 'setIfMissing', path: ['value'], value: []} : null

          // Transform the patches to ensure they work with the nested structure
          const fixedPatches = patches.map(
            (patch: {type?: string; path?: unknown[]; [key: string]: unknown}) => {
              // Ensure patch exists and has required properties
              if (!patch || typeof patch !== 'object') {
                return patch
              }

              if (patch.type === 'insert' && patch.path && Array.isArray(patch.path)) {
                // Ensure the path is correct for the nested structure
                const fixedPath = patch.path[0] === 'value' ? patch.path : ['value', ...patch.path]
                const fixedPatch = {...patch, path: fixedPath}
                return fixedPatch
              }
              return patch
            },
          )

          // If we need to initialize the field, include that patch first
          const allPatches = initPatch ? [initPatch, ...fixedPatches] : fixedPatches

          // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
          return originalOnChange(allPatches as Parameters<typeof originalOnChange>[0])
        }
      }

      // For all other cases, pass through unchanged
      return originalOnChange(patches as Parameters<typeof originalOnChange>[0])
    },
    [originalOnChange, props.value?.value],
  )

  // The parent array contains the languages from the plugin config
  const {languages, languageDisplay, defaultLanguages} = useInternationalizedArrayContext()

  const keyIsValid = languages?.length
    ? Boolean(languages.find((l) => l.id === props.value[LANGUAGE_FIELD_NAME]))
    : false

  const language = languages?.find((l) => l.id === props.value[LANGUAGE_FIELD_NAME])
  const languageTitle: string =
    keyIsValid && language ? getLanguageDisplay(languageDisplay, language.title, language.id) : ''

  const inlineProps = useMemo(
    () => ({
      ...props.inputProps,
      members: props.inputProps.members
        .filter((m) => m.kind === 'field' && m.name === 'value')
        .map((member) => {
          if (member.kind !== 'field') {
            return member
          }

          const field = member.field
          const schemaType = field?.schemaType
          const itemNeedsMigration = !props.value[LANGUAGE_FIELD_NAME]

          const title = itemNeedsMigration ? null : keyIsValid ? (
            <Label muted size={1}>
              {languageTitle}
            </Label>
          ) : (
            <ChangeLanguageButton
              value={props.value}
              path={props.path}
              onChange={originalOnChange}
            />
          )

          return Object.assign({}, member, {
            field: Object.assign({}, field, {
              schemaType: Object.assign({}, schemaType, {title}),
            }),
          })
        }),
      value: props.value,
      // Use our wrapped onChange handler
      onChange: wrappedOnChange,
    }),
    [
      props.inputProps,
      props.value,
      wrappedOnChange,
      languageTitle,
      keyIsValid,
      originalOnChange,
      props.path,
    ],
  )

  const {validation, value, readOnly} = inlineProps

  if (!languages) {
    return <Spinner />
  }

  const isDefault = defaultLanguages.includes(value[LANGUAGE_FIELD_NAME])

  return (
    <Card paddingTop={2} tone={getToneFromValidation(validation)}>
      <Flex align="flex-end" gap={2}>
        <Box flex={1}>{props.inputProps.renderInput(inlineProps)}</Box>
        <RemoveButton
          isDefault={isDefault}
          readOnly={Boolean(readOnly)}
          onChange={originalOnChange}
        />
      </Flex>
    </Card>
  )
}
